<template>
  <q-list>
    <!-- Profile Image -->
    <detail-item
      without-name
      without-icon
      class="q-mb-md"
      :name-spacing="nameSpacing"
    >
      <div class="flex">
        <profile-image
          size="75px"
          additional-icon="mdi-camera"
          clickable
          :error="!!errors['image.id'] ? errors['image.id'][0] : null"
          :initials="initials"
          :profile-image="client.image ? client.image.signed_thumbnail_url : null"
          @click="!client.image ? $refs.fileSelectDialog.open() : undefined"
        >
          <q-menu v-if="client.image !== null">
            <q-card>
              <q-list padding :dense="!$q.platform.has.touch">
                <q-item-wrapper
                  v-close-popup
                  clickable
                  label="View Image"
                  icon="mdi-image-outline"
                  @click="$refs.fileSelectDialog.viewingFileID = client.image.id"
                />
                <q-item-wrapper
                  v-close-popup
                  clickable
                  label="Change Image"
                  icon="mdi-image-search-outline"
                  @click="$refs.fileSelectDialog.open()"
                />
                <q-item-wrapper
                  v-close-popup
                  clickable
                  label="Remove Image"
                  icon="mdi-close"
                  color="red-7"
                  @click="client.image = null"
                />
              </q-list>
            </q-card>
          </q-menu>
        </profile-image>
      </div>
    </detail-item>

    <detail-item
      name="Type"
      with-input
      icon="mdi-account"
      :name-spacing="nameSpacing"
    >
      <q-option-group
        v-model="client.is_individual"
        type="radio"
        inline
        :options="individualOptions"
      />
    </detail-item>

    <detail-item
      v-if="!client.is_individual"
      with-input
      icon="mdi-pencil-outline"
      name="Display Name"
      required
      :name-spacing="nameSpacing"
    >
      <q-input
        v-model="client.display_name"
        dense
        outlined
        :error="!!errors['display_name']"
        :error-message="!!errors['display_name'] ? errors['display_name'][0] : null"
        bottom-slots
        hide-bottom-space
      />
    </detail-item>

    <detail-item
      with-input
      :name="client.is_individual ? 'Name' : 'Contact Name'"
      :required="client.is_individual"
      :icon="client.is_individual ? 'mdi-pencil-outline' : undefined"
      :name-spacing="nameSpacing"
    >
      <div class="row q-col-gutter-sm">
        <div class="col-12 col-sm-6">
          <q-input
            v-model="client.first_name"
            dense
            outlined
            placeholder="First Name"
            :autofocus="mode === 'add'"
            :error="!!firstNameError"
            :error-message="!!firstNameError ? firstNameError[0] : null"
            bottom-slots
            hide-bottom-space
            autocomplete="none"
            @keyup.enter="$refs.lastName.focus()"
          />
        </div>
        <div class="col-12 col-sm-6">
          <q-input
            ref="lastName"
            v-model="client.last_name"
            dense
            outlined
            placeholder="Last Name"
            :error="client.is_individual ? (!!errors['last_name'] || !!errors['display_name']) : !!errors['last_name']"
            :error-message="!!errors['last_name'] ? errors['last_name'][0] : null"
            bottom-slots
            hide-bottom-space1
            autocomplete="none"
          />
        </div>
      </div>
    </detail-item>

    <EmailsPartial
      :errors="errors"
      :client="client"
      :mode="mode"
      :email-list="client.emails"
      :name-spacing="nameSpacing"
      class="q-mb-md"
    />

    <ClientEmailRequest
      v-if="mode === 'add' && !!primaryEmail"
      :email="primaryEmail"
      :client="client"
      :name-spacing="nameSpacing"
    />

    <PhonesPartial
      :errors="errors"
      :phone-list="client.phones"
      :name-spacing="nameSpacing"
      class="q-mb-md"
    />

    <AddressesPartial
      :address-list="client.addresses"
      :errors="errors"
      :name-spacing="nameSpacing"
      class="q-mb-md"
    />

    <q-list class="q-pt-md">
      <sticky-header>
        Communications / Scheduling
      </sticky-header>

      <detail-item
        with-input
        icon="mdi-bell-badge-outline"
        name="Preferred Communication Method"
        :name-spacing="nameSpacing"
      >
        <MessageTypeSelect
          v-model="client.default_communication_method"
          :error="!!errors['default_communication_method']"
          :error-message="!!errors['default_communication_method'] ? errors['default_communication_method'][0] : null"
        />
      </detail-item>

      <detail-item
        v-if="!client.is_individual && contactName"
        tag="label"
        with-input
        :name-spacing="nameSpacing"
      >
        <q-checkbox
          v-model="client.prefer_contact_name"
        >
          <q-item-label>Use Contact Name ({{ contactName }}) on communications with client</q-item-label>
          <q-item-label caption>
            Communications can include appointment emails, reports and invoice reminders.
          </q-item-label>
        </q-checkbox>
      </detail-item>

      <detail-item
        tag="label"
        with-input
        :name-spacing="nameSpacing"
      >
        <q-checkbox
          v-model="client.disable_reminders"
        >
          <q-item-label>Disable Default Reminders</q-item-label>
          <q-item-label caption>
            Avoid creating new reminders for items such as appointment messages and invoice payment reminders.
          </q-item-label>
        </q-checkbox>
      </detail-item>

      <detail-item
        with-input
        icon="mdi-map-clock"
        name="Timezone"
        :name-spacing="nameSpacing"
      >
        <timezones
          v-model="client.timezone"
          outlined
          dense
          clearable
          hint="Leave this blank to automatically inherit your company timezone"
          :error="!!errors['timezone']"
          :error-message="!!errors['timezone'] ? errors['timezone'][0] : null"
        />
      </detail-item>
    </q-list>

    <q-list class="q-pt-md">
      <sticky-header>
        Other Details
      </sticky-header>

      <detail-item
        with-input
        name="Website"
        icon="mdi-web"
        :name-spacing="nameSpacing"
      >
        <q-input
          v-model="client.website"
          outlined
          dense
          placeholder="https://www."
        />
      </detail-item>

      <detail-item
        name="How did you hear about us?"
        icon="mdi-account-voice"
        with-input
        :name-spacing="nameSpacing"
      >
        <q-select
          v-model="client.heard_from_via"
          use-input
          outlined
          dense
          emit-value
          input-debounce="0"
          hint="Press enter to save your own option"
          clearable
          :options="options.clients_heard_from_via"
          @new-value="onCreateNewUniqueValue"
        />
      </detail-item>

      <detail-item
        name="Roles"
        icon="mdi-account-tie"
        with-input
        :name-spacing="nameSpacing"
      >
        <q-select
          v-model="client.role_labels"
          use-input
          use-chips
          outlined
          dense
          emit-value
          input-debounce="0"
          hint="Press enter to save your own role"
          clearable
          multiple
          :options="options.clients_role_labels"
          :error="!!errors['role_labels']"
          :error-message="!!errors['role_labels'] ? errors['role_labels'][0] : null"
          @new-value="onCreateNewUniqueValue"
          @clear="client.role_labels = []"
        />
      </detail-item>

      <detail-item
        icon="mdi-domain"
        with-input
        name="Land Classification"
        :name-spacing="nameSpacing"
      >
        <q-select
          v-model="client.industry_labels"
          use-input
          use-chips
          outlined
          dense
          emit-value
          input-debounce="0"
          hint="Press enter to save your own classification"
          clearable
          multiple
          :options="options.clients_industry_labels"
          :error="!!errors['industry_labels']"
          :error-message="!!errors['industry_labels'] ? errors['industry_labels'][0] : null"
          @new-value="onCreateNewUniqueValue"
          @clear="client.industry_labels = []"
        />
      </detail-item>
    </q-list>

    <q-list class="q-pt-md">
      <sticky-header>
        Documents
      </sticky-header>

      <detail-item
        with-input
        name="Default Invoice Due Date"
        icon="mdi-cash"
        :name-spacing="nameSpacing"
      >
        <q-input
          v-model="client.invoicing_default_due_date"
          type="number"
          outlined
          dense
          clearable
          suffix="Days"
          placeholder="Use System Default"
          :min="0"
          :max="365"
          :error="!!errors['invoicing_default_due_date']"
          :error-message="!!errors['invoicing_default_due_date'] ? errors['invoicing_default_due_date'][0] : null"
          class="full-width-mobile"
          style="max-width: 220px;"
        />
      </detail-item>

      <detail-item
        with-input
        name="Strata Number"
        icon="mdi-id-card"
        :name-spacing="nameSpacing"
      >
        <q-input
          v-model="client.strata_identifier"
          outlined
          dense
          :error="!!errors['strata_identifier']"
          :error-message="!!errors['strata_identifier'] ? errors['strata_identifier'][0] : null"
        />
      </detail-item>

      <detail-item
        with-input
        name="Inventory Markup Percentage"
        icon="mdi-percent"
        :name-spacing="nameSpacing"
      >
        <q-input
          v-model="client.inventory_markup_percentage"
          type="number"
          min="0"
          max="100"
          dense
          outlined
          hide-bottom-space
          suffix="%"
          class="full-width-mobile"
          style="width: 125px;"
          :error="!!errors['inventory_markup_percentage']"
          :error-message="!!errors['inventory_markup_percentage'] ? errors['inventory_markup_percentage'][0] : null"
        />
      </detail-item>
    </q-list>

    <q-list class="q-pt-md">
      <q-separator inset spaced />

      <detail-item
        with-input
        icon="mdi-book-open-page-variant-outline"
        name="Reference Number"
        required
        :name-spacing="nameSpacing"
      >
        <q-input
          v-model="client.reference_id"
          outlined
          class="required-astrx-container"
          hint="A unique identifier for this client"
          :error="!!errors['reference_id']"
          :error-message="!!errors['reference_id'] ? errors['reference_id'][0] : null"
          :counter="!!options.client_reference_id_length"
          :maxlength="options.client_reference_id_length || undefined"
          bottom-slots
          hide-bottom-space
          dense
        />
      </detail-item>

      <detail-item
        v-if="mode === 'add'"
        name="Note"
        with-input
        icon="mdi-note-outline"
        :name-spacing="nameSpacing"
      >
        <q-input
          v-model="client.note"
          outlined
          dense
          placeholder="Enter Note..."
          clearable
          hint="You can add more notes after saving this client."
          :error="!!errors['note']"
          :error-message="!!errors['note'] ? errors['note'][0] : null"
        />
      </detail-item>
    </q-list>

    <file-select-dialog
      ref="fileSelectDialog"
      restrict-to-type="IMAGE"
      :preview-mode="false"
      :selected="client.image"
      @saved="(file) => client.image = file"
      @deleted="client.image = null"
    />
  </q-list>
</template>

<script>
import optionsMixin from '../../mixins/optionsMixin'
import communicationMixin from 'components/partials/communications/communicationMixin'
import PhonesPartial from './Phones'
import AddressesPartial from './Addresses'
import EmailsPartial from './Emails'

import ClientEmailRequest from 'components/app/clients/partials/ClientEmailRequest'
import MessageTypeSelect from 'components/partials/communications/reminders/MessageTypeSelect'

export default {
  components: {
    PhonesPartial, AddressesPartial, EmailsPartial, MessageTypeSelect, ClientEmailRequest
  },
  mixins: [optionsMixin, communicationMixin],
  props: {
    client: {
      required: true,
      type: Object
    },
    errors: {
      type: Object,
      default: () => {
        return {}
      }
    },
    mode: {
      type: String,
      default: 'add'
    },
    options: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      nameSpacing: 175
    }
  },
  computed: {
    contactName () {
      if (!this.client.first_name && !this.client.last_name) {
        return null
      }
      return (this.client.first_name || '') + ' ' + (this.client.last_name || '')
    },
    primaryEmail () {
      const primary = this.client.emails.find(email => email.type === 'PRIMARY')
      return primary ? primary.email_address : null
    },
    initials () {
      if (!this.client || !this.client.display_name) {
        return '#'
      }
      return this.client.display_name.split(' ').map(i => i.charAt(0)).join('').substring(0, 2).toUpperCase()
    },
    firstNameError () {
      if (this.client.is_individual) {
        return this.errors['first_name'] || this.errors['display_name']
      }
      return this.errors['first_name']
    }
  },
  methods: {
    onCreateNewUniqueValue (val, done) {
      done(val, 'add-unique')
    }
  }
}
</script>
